import type { FormEvent } from "react";

import { useDialogForm, useToast } from "@/ds";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import { cancelarEscopoMacro } from "@/modules/financeiro-obras";
import type { DestinoCancelamentoEscopo } from "@/modules/financeiro-obras";
import { useDetalheEscopoMacroDados } from "../../../../hooks/use-planejamento-dados";
import { DESTINO_OBRIGATORIO } from "../campo-destino-cancelamento";
import { JUSTIFICATIVA_OBRIGATORIA } from "@/shared/components";
import { FEL_ZERO, felComContingencia } from "../planejamento-controle.cancelamento";
import {
  AVISO_SEM_PROJECAO,
  AVISO_SEM_PROJECAO_FEL_ZERO,
  CANCELADO_MESSAGE,
  CANCELAR_ESCOPO_FORM_ID,
  CONFIRMAR_EFETIVAR_LABEL,
  CONFIRMAR_LABEL,
  DESTINO_DEVOLVER_AJUDA,
  DESTINO_DEVOLVER_LABEL,
  DESTINO_MANTER_AJUDA,
  DESTINO_MANTER_CONTINGENCIA_AJUDA,
  DESTINO_MANTER_LABEL,
  ENVIADO_PARA_APROVACAO,
  IMPACTO_DEVOLVER,
  IMPACTO_FEL_ZERO,
  IMPACTO_MANTER,
  IMPACTO_MANTER_CONTINGENCIA,
  IMPACTO_NEUTRO,
  PROJECAO_PENDENTE,
  ULTIMO_ESCOPO_MESSAGE,
  ULTIMO_ESCOPO_MESSAGE_FEL_ZERO,
} from "./cancelar-escopo-macro-dialog.constants";
import type {
  CancelarEscopoMacroDialogProps,
  CancelarEscopoMacroEstado,
} from "./types-cancelar-escopo-macro-dialog";

type CancelarEscopoForm = { justificativa: string; destino: DestinoCancelamentoEscopo | "" };

export function useCancelarEscopoMacroDialog({
  obraId,
  escopo,
  onClose,
  onConcluido,
}: CancelarEscopoMacroDialogProps) {
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const consulta = useDetalheEscopoMacroDados(obraId, escopo.obraEscopoMacroId);
  const detalhe = consulta.data;

  let estado: CancelarEscopoMacroEstado = "pronto";
  if (consulta.status === "forbidden") estado = "proibido";
  else if (consulta.status === "error") estado = "erro";
  else if (consulta.status === "loading" || !detalhe) estado = "carregando";

  const efetivacaoImediata = escopo.fel === FEL_ZERO;
  const contingencia = felComContingencia(escopo.fel);
  const semProjecao = detalhe ? !detalhe.possuiProjecao : false;
  const pedeDestino = estado === "pronto" && !efetivacaoImediata && !semProjecao;

  const form = useDialogForm<CancelarEscopoForm>({
    initial: { justificativa: "", destino: "" },
    validate: (valores) => {
      const erros: Partial<Record<keyof CancelarEscopoForm, string>> = {};
      if (valores.justificativa.trim() === "") erros.justificativa = JUSTIFICATIVA_OBRIGATORIA;
      if (pedeDestino && valores.destino === "") erros.destino = DESTINO_OBRIGATORIO;
      return erros;
    },
    onSubmit: async (valores) => {
      const body = {
        justificativa: valores.justificativa.trim(),
        ...(pedeDestino && valores.destino ? { destino: valores.destino } : {}),
      };
      const resposta = await comChave(body, (idempotencyKey) =>
        cancelarEscopoMacro(obraId, escopo.obraEscopoMacroId, body, { idempotencyKey }),
      );
      if (resposta.situacao === "APROVADA") toast.success(CANCELADO_MESSAGE);
      else toast.info(ENVIADO_PARA_APROVACAO);
      onConcluido();
    },
  });

  const { justificativa, destino } = form.values;
  const descarte = useConfirmarDescarte({
    onDescartar: onClose,
    testId: "cancelar-escopo-descarte",
  });

  let avisoImpacto: string | null = null;
  if (estado === "pronto" && !semProjecao) {
    if (efetivacaoImediata) avisoImpacto = IMPACTO_FEL_ZERO;
    else if (destino === "DEVOLVER") avisoImpacto = IMPACTO_DEVOLVER;
    else if (destino === "MANTER") {
      avisoImpacto = contingencia ? IMPACTO_MANTER_CONTINGENCIA : IMPACTO_MANTER;
    } else avisoImpacto = IMPACTO_NEUTRO;
  }

  const avisoSemProjecao = efetivacaoImediata ? AVISO_SEM_PROJECAO_FEL_ZERO : AVISO_SEM_PROJECAO;
  const ultimoEscopo = efetivacaoImediata ? ULTIMO_ESCOPO_MESSAGE_FEL_ZERO : ULTIMO_ESCOPO_MESSAGE;

  return {
    formId: CANCELAR_ESCOPO_FORM_ID,
    estado,
    errorMessage: consulta.errorMessage,
    recarregar: consulta.reload,
    escopo,
    projecaoTexto:
      semProjecao || !escopo.projecao
        ? PROJECAO_PENDENTE
        : `${escopo.projecao} · ${escopo.classificacao}`,
    confirmarLabel: efetivacaoImediata ? CONFIRMAR_EFETIVAR_LABEL : CONFIRMAR_LABEL,
    destino: pedeDestino
      ? {
          value: destino === "" ? null : destino,
          error: form.errors.destino ?? null,
          opcoes: [
            {
              valor: "DEVOLVER" as const,
              testId: "cancelar-escopo-destino-devolver",
              label: DESTINO_DEVOLVER_LABEL,
              ajuda: DESTINO_DEVOLVER_AJUDA,
            },
            {
              valor: "MANTER" as const,
              testId: "cancelar-escopo-destino-manter",
              label: DESTINO_MANTER_LABEL,
              ajuda: contingencia
                ? DESTINO_MANTER_CONTINGENCIA_AJUDA
                : DESTINO_MANTER_AJUDA(escopo.tipoObra),
            },
          ],
        }
      : null,
    avisoImpacto,
    avisoSemProjecao: semProjecao ? avisoSemProjecao : null,
    avisoUltimoEscopo: detalhe?.ultimoEscopoDoTipo ? ultimoEscopo(escopo.tipoObra) : null,
    justificativa,
    justificativaError: form.errors.justificativa ?? null,
    isSubmitting: form.isSubmitting,
    submitError: form.submitError,
    confirmarDesabilitado: estado !== "pronto",
    escolherDestino: (valor: DestinoCancelamentoEscopo) => form.setValue("destino", valor),
    digitarJustificativa: (texto: string) => form.setValue("justificativa", texto),
    enviar: (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      void form.submit();
    },
    cancelar: () => descarte.pedir(form.isDirty),
    descarte: descarte.confirmacao,
  };
}
